import { ReactNode, createContext, useState } from 'react';

import { useDisclosure } from '@chakra-ui/react';
import { Resource, ResourceVersion } from '@duke-hq/svc-api-db';

interface ResourceContentInterface extends Resource {
  draftVersion: ResourceVersion;
}

/**
 * this is used even for the learner even though learner cannot edit this, we still use editor to render the content
 */
function usePageEditorState() {
  const [saved, setSaved] = useState(true);
  const [resourceContent, setResourceContent] = useState(null);
  const [resourceSettings, setResourceSettings] = useState<Partial<ResourceContentInterface>>();

  const {
    getButtonProps: pageSettingsButtonProps,
    getDisclosureProps: pageSettingsDisclosure,
    isOpen: settingsIsOpen,
  } = useDisclosure();

  const {
    getButtonProps: courseSettingsButtonProps,
    getDisclosureProps: courseSettingsDisclosure,
    isOpen: courseSettingsIsOpen,
  } = useDisclosure();

  const handleUpdateResourceSettings = <T extends keyof ResourceContentInterface>(
    key: T,
    value: ResourceContentInterface[T],
  ) => {
    setResourceSettings((prev) => {
      const updatedValues = { ...prev };
      updatedValues[key] = value;
      return updatedValues;
    });
  };

  const [hiddenPageSettings, setHiddenPageSettings] = useState(!settingsIsOpen);
  const [hiddenCourseSettings, setHiddenCourseSettings] = useState(!courseSettingsIsOpen);
  const [isCourseSettingsExpanded, setIsCourseSettingsExpanded] = useState(false);

  // Route-wide: consumers must match `resourceId` or a publish leaks into other guides' pills.
  const [lastPublishedResourceVersion, setLastPublishedResourceVersion] = useState<{
    id: number;
    resourceId: string;
  } | null>(null);

  return {
    resourceContent,
    setResourceContent,
    saved,
    setSaved,
    hiddenPageSettings,
    setHiddenPageSettings,
    pageSettingsButtonProps,
    pageSettingsDisclosure,
    setResourceSettings,
    resourceSettings,
    handleUpdateResourceSettings,
    settingsIsOpen,

    hiddenCourseSettings,
    setHiddenCourseSettings,
    courseSettingsButtonProps,
    courseSettingsDisclosure,
    courseSettingsIsOpen,
    isCourseSettingsExpanded,
    setIsCourseSettingsExpanded,
    lastPublishedResourceVersion,
    setLastPublishedResourceVersion,
  };
}

export const PageEditorStateContext = createContext<ReturnType<typeof usePageEditorState>>(
  {} as any,
);

export function PageEditorStateProvider({ children }: { children: ReactNode }) {
  const pageEditorState = usePageEditorState();
  return (
    <PageEditorStateContext.Provider value={pageEditorState}>
      {children}
    </PageEditorStateContext.Provider>
  );
}
